import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import db from '../services/database';
import Icon from '../components/UI/Icon';
import './JournalPage.css';

// Reflection prompts for journaling
const REFLECTION_PROMPTS = [
  "What does this passage reveal about God's character?",
  "How can I apply this truth to my life today?",
  "What stands out to me in this reading?",
  "Is there a promise, command, or warning here?",
  "What does this teach me about prayer?",
  "How does this connect to other scriptures I know?",
  "What questions does this passage raise?",
  "What am I grateful for today?",
  "How have I seen God's faithfulness recently?",
  "What is God teaching me in this season?"
];

function JournalPage() {
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPrompts, setShowPrompts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load journal entries
  useEffect(() => {
    async function loadEntries() {
      try {
        const journalEntries = await db.journals.orderBy('createdAt').reverse().toArray();
        setEntries(journalEntries);
      } catch (err) {
        console.error('Failed to load journal entries:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadEntries();
  }, []);

  // Create new entry
  const handleNewEntry = useCallback(() => {
    setSelectedEntry(null);
    setEditTitle(`Journal - ${new Date().toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`);
    setEditContent('');
    setEditTags([]);
    setIsEditing(true);
  }, []);

  // Save entry
  const handleSave = useCallback(async () => {
    const entry = {
      title: editTitle,
      content: editContent,
      tags: editTags,
      date: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString()
    };

    try {
      if (selectedEntry) {
        await db.journals.update(selectedEntry.id, entry);
        setEntries(prev => prev.map(e => 
          e.id === selectedEntry.id ? { ...e, ...entry } : e
        ));
      } else {
        entry.createdAt = new Date().toISOString();
        const id = await db.journals.add(entry);
        setEntries(prev => [{ ...entry, id }, ...prev]);
      }
      setIsEditing(false);
      setSelectedEntry(null);
    } catch (err) {
      console.error('Failed to save entry:', err);
    }
  }, [editTitle, editContent, editTags, selectedEntry]);

  // Delete entry
  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Are you sure you want to delete this journal entry?')) {
      try {
        await db.journals.delete(id);
        setEntries(prev => prev.filter(e => e.id !== id));
        if (selectedEntry?.id === id) {
          setSelectedEntry(null);
          setIsEditing(false);
        }
      } catch (err) {
        console.error('Failed to delete entry:', err);
      }
    }
  }, [selectedEntry]);

  // Select entry for viewing/editing
  const handleSelectEntry = useCallback((entry) => {
    setSelectedEntry(entry);
    setEditTitle(entry.title);
    setEditContent(entry.content);
    setEditTags(entry.tags || []);
    setIsEditing(false);
  }, []);

  // Add tag
  const handleAddTag = useCallback(() => {
    if (newTag.trim() && !editTags.includes(newTag.trim())) {
      setEditTags(prev => [...prev, newTag.trim()]);
      setNewTag('');
    }
  }, [newTag, editTags]);

  // Remove tag
  const handleRemoveTag = useCallback((tag) => {
    setEditTags(prev => prev.filter(t => t !== tag));
  }, []);

  // Insert prompt
  const handleInsertPrompt = useCallback((prompt) => {
    setEditContent(prev => prev + (prev ? '\n\n' : '') + prompt + '\n');
    setShowPrompts(false);
  }, []);

  // Filter entries
  const filteredEntries = searchQuery
    ? entries.filter(e => 
        e.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : entries;

  // Group entries by date
  const groupedEntries = filteredEntries.reduce((groups, entry) => {
    const date = entry.date || entry.createdAt?.split('T')[0];
    if (!groups[date]) groups[date] = [];
    groups[date].push(entry);
    return groups;
  }, {});

  // Handle back to list on mobile
  const handleBackToList = useCallback(() => {
    setSelectedEntry(null);
    setIsEditing(false);
  }, []);

  // Determine mobile view state
  const mobileViewClass = isEditing ? 'editing' : selectedEntry ? 'viewing' : '';

  return (
    <div className={`journal-page page ${mobileViewClass}`}>
      <div className="journal-sidebar">
        <div className="sidebar-header">
          <div className="header-content">
            <h1 className="page-title">Journal</h1>
            <p className="page-subtitle">Your personal reflections</p>
          </div>
          <button className="btn btn-primary btn-new-entry" onClick={handleNewEntry}>
            <Icon name="plus" size={20} />
            New Entry
          </button>
        </div>

        <div className="search-container">
          <Icon name="search" size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>

        <div className="entries-list">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading entries...</p>
            </div>
          ) : filteredEntries.length === 0 && searchQuery ? (
            <div className="empty-state">
              <Icon name="search" size={40} />
              <p>No entries match "{searchQuery}"</p>
              <button className="btn btn-ghost btn-sm" onClick={() => setSearchQuery('')}>
                Clear search
              </button>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="empty-state">
              <Icon name="journal" size={48} />
              <p className="empty-hint">No entries yet</p>
              <button className="btn btn-primary btn-sm" onClick={handleNewEntry}>
                Write your first entry
              </button>
            </div>
          ) : (
            Object.entries(groupedEntries).map(([date, dateEntries]) => (
              <div key={date} className="date-group">
                <div className="date-header">
                  {new Date(date).toLocaleDateString('en-US', { 
                    weekday: 'short',
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
                {dateEntries.map(entry => (
                  <button
                    key={entry.id}
                    className={`entry-item ${selectedEntry?.id === entry.id ? 'active' : ''}`}
                    onClick={() => handleSelectEntry(entry)}
                  >
                    <div className="entry-title">{entry.title}</div>
                    <div className="entry-preview">
                      {entry.content?.substring(0, 80)}...
                    </div>
                    {entry.tags?.length > 0 && (
                      <div className="entry-tags">
                        {entry.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="sidebar-footer">
          <div className="privacy-notice">
            <Icon name="info" size={12} />
            <p>
              <strong>Private & local.</strong> Your entries stay on this device.{' '}
              <Link to="/settings">Export backups</Link>
            </p>
          </div>
        </div>
      </div>

      <div className="journal-content">
        {/* Mobile back button */}
        <button className="mobile-back-btn" onClick={handleBackToList}>
          <Icon name="chevronLeft" size={20} />
          Back to entries
        </button>

        {isEditing ? (
          <div className="editor-container paper-texture">
            <input
              type="text"
              className="entry-title-input"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Entry title..."
            />

            <div className="editor-toolbar">
              <button 
                className="btn btn-ghost btn-sm"
                onClick={() => setShowPrompts(!showPrompts)}
              >
                <Icon name="themes" size={16} />
                Add Prompt
              </button>
            </div>

            {showPrompts && (
              <div className="prompts-dropdown">
                {REFLECTION_PROMPTS.map((prompt, index) => (
                  <button
                    key={index}
                    className="prompt-item"
                    onClick={() => handleInsertPrompt(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            <textarea
              className="entry-content-input"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Write your thoughts, reflections, and prayers..."
            />

            <div className="tags-section">
              <div className="tags-list">
                {editTags.map(tag => (
                  <span key={tag} className="tag editable">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)}>
                      <Icon name="x" size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="tag-input-container">
                <input
                  type="text"
                  className="tag-input"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  placeholder="Add tag..."
                />
                <button className="btn btn-ghost btn-sm" onClick={handleAddTag}>
                  Add
                </button>
              </div>
            </div>

            <div className="editor-actions">
              <button 
                className="btn btn-ghost"
                onClick={() => {
                  setIsEditing(false);
                  if (!selectedEntry) setSelectedEntry(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!editContent.trim()}
              >
                <Icon name="check" size={18} />
                Save Entry
              </button>
            </div>
          </div>
        ) : selectedEntry ? (
          <div className="entry-view paper-texture">
            <div className="entry-header">
              <h2 className="entry-title">{selectedEntry.title}</h2>
              <div className="entry-actions">
                <button 
                  className="btn btn-ghost btn-icon"
                  onClick={() => setIsEditing(true)}
                >
                  <Icon name="edit" size={18} />
                </button>
                <button 
                  className="btn btn-ghost btn-icon"
                  onClick={() => handleDelete(selectedEntry.id)}
                >
                  <Icon name="trash" size={18} />
                </button>
              </div>
            </div>
            
            <div className="entry-meta">
              <span className="entry-date">
                {new Date(selectedEntry.createdAt).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>

            {selectedEntry.tags?.length > 0 && (
              <div className="entry-tags">
                {selectedEntry.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            )}

            <div className="entry-body">
              {selectedEntry.content.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        ) : entries.length > 0 ? (
          <div className="no-selection">
            <Icon name="journal" size={48} />
            <h3>Select an entry to view</h3>
            <p>Or create a new journal entry</p>
          </div>
        ) : (
          <div className="no-selection empty-state-main">
            <Icon name="journal" size={64} />
            <h3>Start Your Journal</h3>
            <p>Your journal is a private space for reflection and prayer</p>
            <button className="btn btn-primary" onClick={handleNewEntry}>
              <Icon name="plus" size={18} />
              Write Your First Entry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default JournalPage;
